import { inject, injectable } from "@launchtray/tsyringe-async";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { IGiftBoxPopupController } from "./IGiftBoxPopupController";
import { IContextResolver } from "by/gamefactory/cityversetycoon/composition/IContextResolver";
import { HourlyBonusState } from "../../hourlyBonus/mainHourlyBonus/HourlyBonusState";
import { HourlyBonusModule } from "../../hourlyBonus/HourlyBonusModule";
import { HourlyBonusType } from "../../hourlyBonus/HourlyBonusExecutionOptions";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { GiftDiceRollsAction } from "../../../game/flow/impl/diceActions/GiftDiceRollsAction";
import { DiceComboAction } from "../../../game/flow/impl/diceActions/DiceComboAction";
import { GiftType } from "../../../game/flow/impl/diceActions/GiftType";
import { GiftShieldsAction } from "../../../game/flow/impl/diceActions/GiftShieldsAction";
import { GiftPiggyWheelAction } from "../../../game/flow/impl/diceActions/GiftPiggyWheelAction";

@injectable()
export class GiftBoxPopupController implements IGiftBoxPopupController {
    private readonly contextResolver: IContextResolver;
    private readonly networkClient: INetworkClient;
    private readonly playerModel: IPlayerModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    public constructor(
        @inject("IContextResolver") contextResolver: IContextResolver,
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        this.contextResolver = contextResolver;
        this.networkClient = networkClient;
        this.playerModel = playerModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
    }

    public async claimGiftReward(): Promise<DiceComboAction> {
        const result = await this.networkClient.claimGiftReward();
        switch (result.type) {
            case GiftType.GIFT_DICE_ROLLS:
                return new GiftDiceRollsAction(result.rewardInDiceRolls);
            case GiftType.GIFT_SHIELDS:
                return new GiftShieldsAction(result.rewardInShields);
            case GiftType.GIFT_PIGGY_WHEEL:
                return new GiftPiggyWheelAction();
            default:
                throw new Error(`Failed to convert gift action: ${result.type}. No Action defined for it.`);
        }
    }

    public collectRollsHandle(rollsValue: number): void {
        this.playerModel.addRolls(rollsValue);
    }

    public collectShieldHandle(shieldsValue: number): void {
        this.playerModel.addShields(shieldsValue);
    }

    public async showHourlyBonusPopup(state: HourlyBonusState): Promise<void> {
        try {
            const module = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(HourlyBonusModule));
            await module.start({ type: HourlyBonusType.MAIN, args: state });
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing hourly bonus popup: ", e);
        }
    }
}
